import {
    Puzzle,
    AlertCircle,
    Search,
    Plus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getWidgets } from '@/api/widgets/widgets.api';
import type { WidgetResponse } from '@/api/widgets/widgets.types';
import { useEnhancedToast } from "@/components/ui/enhanced-toast"
import { WidgetCard, WidgetCardSkeleton } from './components/WidgetCard';
import { containerVariants, itemVariants } from '@/components/animations';
import { useAuth } from '@/context/AuthContext';
import { SiteHeader } from '@/components/site-header';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';


const Widgets = () => {
    const [widgets, setWidgets] = useState<WidgetResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();
    const { error } = useEnhancedToast();
    const errorRef = useRef(error);
    const { user } = useAuth();

    const canCreateWidget = user?.permissions?.widgets === 'EDITOR';

    // Update ref when error function changes
    useEffect(() => {
        errorRef.current = error;
    }, [error]);

    const fetchWidgets = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getWidgets();
            setWidgets(response.data ?? []);
        } catch (err) {
            if (err instanceof Error) {
                errorRef.current({
                    icon: AlertCircle,
                    title: "Error",
                    description: err.message || "Failed to fetch widgets",
                    duration: 4000,
                });
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchWidgets();
    }, [fetchWidgets]);

    // Filter widgets based on search query
    const filteredWidgets = useMemo(() => {
        if (!searchQuery.trim()) return widgets;
        
        const query = searchQuery.toLowerCase();
        return widgets.filter(widget => 
            widget.name.toLowerCase().includes(query) ||
            widget.id.toLowerCase().includes(query)
        );
    }, [widgets, searchQuery]);

    return (
        <div className="h-full flex flex-col">
            <SiteHeader
                title="Widgets"
                subtitle="Create and manage customizable widgets"
            >
                {!isLoading && widgets.length > 0 && (
                    <>
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                                placeholder="Search widgets..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 w-[250px] lg:w-[300px] pl-9"
                            />
                        </div>

                        {/* New Widget Button */}
                        <Button
                            onClick={() => canCreateWidget && navigate('/dashboard/widgets/new')}
                            disabled={!canCreateWidget}
                            size="sm"
                            className="gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            New Widget
                        </Button>
                    </>
                )}
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                {isLoading ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, index) => (
                                <WidgetCardSkeleton key={index} />
                            ))}
                        </div>
                    </motion.div>
                ) : widgets.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="w-full -mt-12"
                        >
                            <EmptyState
                                icon={Puzzle}
                                title="No Widgets Yet"
                                description="Create advanced customizable widgets for your AI assistants with enhanced features and integrations."
                                action={{
                                    label: "New Widget",
                                    onClick: () => navigate('/dashboard/widgets/new'),
                                    disabled: !canCreateWidget,
                                    disabledReason: "You need editor permissions to create widgets. Contact your administrator for access.",
                                }}
                            />
                        </motion.div>
                    </div>
                    ) : filteredWidgets.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="flex items-center justify-center"
                            style={{ height: 'calc(100vh - 200px)' }}
                        >
                            <div className="text-center">
                                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                    <Search className="h-8 w-8 text-muted-foreground" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2">No Matching Widgets</h3>
                                <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                    We couldn't find any widgets matching your search.
                                </p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSearchQuery('')}
                                >
                                    Clear Search
                                </Button>
                            </div>
                        </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <motion.div 
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                        >
                            {filteredWidgets.map((widget) => (
                                <WidgetCard
                                    key={widget.id}
                                    widget={widget}
                                            onClick={() => navigate(`/dashboard/widgets/${widget.id}`)}
                                    variants={itemVariants}
                                />
                            ))}
                        </motion.div>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default Widgets;
